import React, { useState, useEffect, useCallback } from "react";

import dayjs, { ISO_8601 } from "dayjs-local";
import firebase from "firebaseFactory";
import { useInterval } from "hooks";

import { isDeviceOnline, deviceOnlineInterval } from "..";

export const useDeviceLastSeen = (
  siteId: string,
  unitId: string,
  deviceId: string
) => {
  const [lastSeen, setLastSeen] = useState<dayjs.Dayjs | undefined>();

  const updateLastSeen = useCallback(async () => {
    const timestampSnapshot = await firebase
      .database(firebase.shards.Customer)
      .ref(
        `/devices/indexBySite/${siteId}/latest/${unitId}/${deviceId}/timestamp_utc`
      )
      .once("value");
    setLastSeen(
      timestampSnapshot.val() && dayjs.utc(timestampSnapshot.val(), ISO_8601)
    );
  }, [siteId, unitId, deviceId]);

  useEffect(() => {
    updateLastSeen();
  }, [updateLastSeen]);

  useInterval(() => {
    updateLastSeen();
  }, deviceOnlineInterval / 3);
  return { lastSeen };
};

interface DeviceStatusIconProps {
  lastSeen: dayjs.Dayjs | undefined;
  className?: string;
  deviceName?: string;
}

// TODO: This should actually be removed entirely and replaced with
// AggregatedDeviceStatus so that we look at the state of the device
// (whether it has a fault) in addition to when it last sent data
export const DeviceStatusIcon = ({
  deviceName,
  lastSeen,
  className,
  ...props
}: DeviceStatusIconProps & React.HTMLAttributes<HTMLDivElement>) => {
  const [currentTime, setCurrentTime] = useState<number>(Date.now());
  const isOnline = isDeviceOnline(lastSeen, currentTime);
  useInterval(() => {
    setCurrentTime(Date.now());
  }, deviceOnlineInterval);

  return (
    <div
      className={`rounded-full w-4 h-4 py-1 px-1 ${
        isOnline ? "bg-green-400" : "bg-gray-400"
      } ${className || ""}`}
      title={`Device ${deviceName ? deviceName + " " : ""}${
        isOnline ? "online" : "offline"
      } - last seen ${
        (lastSeen && lastSeen.isValid() && lastSeen.toISOString()) || "never"
      }`}
      {...props}
    ></div>
  );
};

export const DeviceStatus = ({
  siteId,
  unitId,
  deviceId,
  className,
}: {
  siteId: string;
  unitId: string;
  deviceId: string;
  className?: string;
}) => {
  const { lastSeen } = useDeviceLastSeen(siteId, unitId, deviceId);
  return (
    <DeviceStatusIcon
      deviceName={`${siteId}/${unitId}/${deviceId}`}
      lastSeen={lastSeen}
      className={className}
    />
  );
};
